import type { User, Label as LabelModel, Attachment, Issue, Project, ProjectAttachment } from "./types";

// Shared Issue shape
const issueFragment = `
id
identifier
title
url
priorityLabel
createdAt
updatedAt
labels {
  nodes {
    id
    name
    color
  }
}
cycle {
  id
  number
  startsAt
  endsAt
  completedAt
}
project {
  name
}
team {
  id
  name
}
state {
  id
  name
  color
}
assignee {
  avatarUrl
  name
}
customerTicketCount
`;

/** Reuses the project fields needed across linked-project UI and search results in the Zendesk app. */
const projectFragment = `
id
identifier
name
url
createdAt
updatedAt
teams {
  nodes {
    id
    name
    key
  }
}
status {
  id
  name
  color
}
priorityLabel
`;

/** Fetches one team with the members and labels needed to populate the create-issue form. */
export const TeamQuery = `
  query ($teamId: String!, $after: String) {
    team(id: $teamId) {
      id
      name
      labels(first: 250, after: $after) {
        nodes {
          id
          name
          color
          parent {
            id
            name
            color
            groupType
          }
          retiredAt
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      members(first: 250) {
        nodes {
          id
          name
          email
          avatarUrl
        }
      }
    }
  }
`;

/** Describes the response returned by {@link TeamQuery}. */
export interface ITeamQuery {
  team?: {
    id: string;
    name: string;
    labels: {
      nodes: LabelModel[];
      pageInfo: {
        hasNextPage: boolean;
        endCursor: string;
      };
    };
    members: {
      nodes: User[];
    };
  };
}

/** Searches the viewer's accessible teams while the create form is filtering possible destinations. */
export const TeamSearchQuery = `
  query ($first: Int!, $filter: TeamFilter) {
    teams(first: $first, filter: $filter) {
      nodes {
        id
        name
        displayName
        retiredAt
      }
    }
  }
`;

/** Describes the response returned by {@link TeamSearchQuery}. */
export interface ITeamSearchQuery {
  teams: {
    nodes: {
      id: string;
      name: string;
      displayName: string;
      retiredAt?: string;
    }[];
  };
}

/** Reloads a previously selected team when local state references a team outside the current search results. */
export const ActiveTeamQuery = `
  query ($teamId: ID!) {
    teams(first: 1, filter: { id: { eq: $teamId } }) {
      nodes {
        id
        name
        displayName
        retiredAt
      }
    }
  }
`;

/** Describes the response returned by {@link ActiveTeamQuery}. */
export interface IActiveTeamQuery {
  teams: {
    nodes: {
      id: string;
      name: string;
      displayName: string;
      retiredAt?: string;
    }[];
  };
}

/** Searches Linear issues so agents can attach an existing issue to the current Zendesk ticket. */
export const SearchIssueQuery = `
  query ($searchQuery: String!, $includeComments: Boolean!) {
    searchIssues(term: $searchQuery, includeComments: $includeComments, first: 25) {
      nodes {
        ${issueFragment}
      }
    }
  }
`;

/** Searches both issues and projects for workspaces that allow either link target from Zendesk. */
export const CombinedSearchQuery = `
  query ($searchQuery: String!, $includeComments: Boolean!) {
    searchIssues(term: $searchQuery, includeComments: $includeComments, first: 25) {
      nodes {
        ${issueFragment}
      }
    }
    searchProjects(term: $searchQuery, first: 25) {
      nodes {
        ${projectFragment}
      }
    }
  }
`;

/** Describes the response returned by {@link CombinedSearchQuery}. */
export interface ICombinedSearchQuery {
  searchIssues: {
    nodes: Issue[];
  };
  searchProjects: {
    nodes: Project[];
  };
}

/** Creates a new Linear issue from the Zendesk ticket details gathered in the create flow. */
export const CreateIssueMutation = `
  mutation IssueCreateMutation(
    $teamId: String!
    $title: String!
    $description: String
    $assigneeId: String
    $labelIds: [String!]
    $priority: Int
    $templateId: String
  ) {
    issueCreate(input: {
      teamId: $teamId
      title: $title
      description: $description
      assigneeId: $assigneeId
      labelIds: $labelIds
      priority: $priority
      templateId: $templateId
    }) {
      success
      issue {
        id
      }
    }
  }
`;
/** Describes the response returned by {@link CreateIssueMutation}. */
export interface ICreateIssueMutation {
  issueCreate: {
    success: boolean;
    issue?: {
      id: string;
    };
  };
}

/** Attaches the current Zendesk ticket to an existing or newly created Linear issue. */
export const CreateAttachmentMutation = `
  mutation IssueAttachmentMutation(
    $issueId: String!
    $title: String!
    $subtitle: String
    $url: String!
    $metadata: JSONObject
    $commentBody: String
  ) {
    attachmentCreate(input: {
      issueId: $issueId
      title: $title
      subtitle: $subtitle
      url: $url
      metadata: $metadata
      commentBody: $commentBody
    }) {
      success
      attachment {
        id
      }
    }
  }
`;

/** Describes the response returned by {@link CreateAttachmentMutation}. */
export interface ICreateAttachmentMutation {
  attachmentCreate: {
    success: boolean;
    attachment?: {
      id: string;
    };
  };
}
/** Removes a Zendesk-to-issue attachment when an agent unlinks a ticket from a Linear issue. */
export const DeleteAttachmentMutation = `
  mutation IssueAttachmentMutation(
    $id: String!
  ) {
    attachmentDelete(id: $id) {
      success
    }
  }
`;

/** Attaches the current Zendesk ticket to a Linear project for customer-facing project tracking. */
export const CreateProjectAttachmentMutation = `
  mutation ProjectAttachmentMutation(
    $projectId: String!
    $title: String!
    $subtitle: String
    $url: String!
    $metadata: JSONObject
    $noteBody: String
  ) {
    projectAttachmentCreate(input: {
      projectId: $projectId
      title: $title
      subtitle: $subtitle
      url: $url
      metadata: $metadata
      noteBody: $noteBody
    }) {
      success
      attachment {
        id
      }
    }
  }
`;

/** Describes the response returned by {@link CreateProjectAttachmentMutation}. */
export interface ICreateProjectAttachmentMutation {
  projectAttachmentCreate: {
    success: boolean;
    attachment?: {
      id: string;
    };
  };
}

/** Describes the response returned by {@link DeleteAttachmentMutation}. */
export interface IDeleteAttachmentMutation {
  attachmentDelete: {
    success: boolean;
  };
}

/** Removes a Zendesk-to-project attachment when an agent unlinks a ticket from a Linear project. */
export const DeleteProjectAttachmentMutation = `
  mutation ProjectAttachmentMutation(
    $id: String!
  ) {
    projectAttachmentDelete(id: $id) {
      success
    }
  }
`;

/** Describes the response returned by {@link DeleteProjectAttachmentMutation}. */
export interface IDeleteProjectAttachmentMutation {
  projectAttachmentDelete: {
    success: boolean;
  };
}

/** Adds a comment to a Linear issue when the link flow includes Zendesk context as a follow-up note. */
export const CreateCommentMutation = `
  mutation IssueCommentMutation(
    $issueId: String!
    $body: String!
  ) {
    commentCreate(input: {
      issueId: $issueId
      body: $body
    }) {
      success
      comment {
        id
      }
    }
  }
`;

/** Describes the response returned by {@link CreateCommentMutation}. */
export interface ICreateCommentMutation {
  commentCreate: {
    success: boolean;
    comment?: {
      id: string;
    };
  };
}

/** Loads the viewer capabilities needed to decide which create and link flows the Zendesk app can offer. */
export const ViewerQuery = `
  query {
    viewer {
      id
      name
      email
      avatarUrl
    }
    teams(first: 50) {
      nodes {
        id
        name
        displayName
        retiredAt
      }
    }
    issuePriorityValues {
      priority
      label
    }
    templatesForIntegration(integrationType: "zendesk") {
      id
      name
      templateData
      team {
        id
        name
      }
      createdAt
      sortOrder
    }
    organization {
      customersEnabled
    }
    issueIntakeEnabledForZendesk
  }
`;

/** Describes the response returned by {@link ViewerQuery}. */
export interface IViewerQuery {
  viewer: User;
  teams: {
    nodes: {
      id: string;
      name: string;
      displayName: string;
      retiredAt?: string;
    }[];
  };
  issuePriorityValues: {
    priority: number;
    label: string;
  }[];
  templatesForIntegration: {
    id: string;
    name: string;
    templateData: string;
    team?: {
      id: string;
      name: string;
    };
    createdAt: string;
    sortOrder: number;
  }[];
  organization: {
    customersEnabled: boolean;
  };
  issueIntakeEnabledForZendesk: boolean;
}

/** Loads the issue and project attachments that already point back to the current Zendesk ticket URL. */
export const AttachmentsQuery = `
  query AttachmentQuery($ticketUrl: String!) {
    attachmentsForURL(url: $ticketUrl) {
      nodes {
        id
        createdAt
        issue {
          ${issueFragment}
        }
      }
    }
    projectAttachments(filter: {url:{eq:$ticketUrl}}) {
      nodes {
        id
        createdAt
        project {
          ${projectFragment}
        }
      }
    }
  }
`;

/** Describes the response returned by {@link AttachmentsQuery}. */
export interface IAttachmentsQuery {
  attachmentsForURL: {
    nodes: Attachment[];
  };
  projectAttachments: {
    nodes: ProjectAttachment[];
  };
}

/** Finds likely duplicate or related issues from the current ticket subject and first message content. */
export const SimilarIssuesQuery = `
  query SimilarIssuesQuery($ticketContent: String!) {
    similarIssues(text: $ticketContent, first: 3) {
      nodes {
        issue {
          ${issueFragment}
        }
      }
    }
  }
`;

/** Describes the response returned by {@link SimilarIssuesQuery}. */
export interface ISimilarIssuesQuery {
  similarIssues: {
    nodes: {
      issue: Issue;
    }[];
  };
}

/** Starts the OAuth string-auth flow that the embedded Zendesk app can complete via a popup and polling. */
export const oauthAuthStringChallengeMutation = `
mutation OauthAuthStringChallenge($appId: String! $scope: [String!]! $codeChallenge: String!) {
  oauthAuthStringChallenge(appId: $appId, scope: $scope, codeChallenge: $codeChallenge) {
    success
    authString
  }
}
`;

/** Describes the response returned by {@link oauthAuthStringChallengeMutation}. */
export interface OauthAuthStringChallengePayload {
  oauthAuthStringChallenge: {
    success: boolean;
    authString: string;
  };
}

/** Checks whether the popup OAuth flow has completed and returns an access token when it has. */
export const oauthAuthStringCheckMutation = `
  mutation OauthAuthStringCheck($appId: String! $authString: String! $codeVerifier: String!) {
    oauthAuthStringCheck(appId: $appId, authString: $authString, codeVerifier: $codeVerifier) {
      success
      token
    }
  }
`;

/** Describes the response returned by {@link oauthAuthStringCheckMutation}. */
export interface OauthAuthStringCheckPayload {
  oauthAuthStringCheck: {
    success: boolean;
    token?: string;
  };
}

/** Triggers Linear Agent issue-intake processing for the current Zendesk ticket and optional instructions. */
export const IssueIntakeProcessZendeskTicketMutation = `
  mutation ($ticketId: Int!, $ticketUrl: String!, $latestMessageTimestamp: String, $directive: String) {
    issueIntakeProcessZendeskTicket(ticketId: $ticketId, ticketUrl: $ticketUrl, latestMessageTimestamp: $latestMessageTimestamp, directive: $directive) {
      success
      issues {
        id
        identifier
        title
        url
      }
    }
  }
`;

/** Describes the response returned by {@link IssueIntakeProcessZendeskTicketMutation}. */
export interface IIssueIntakeProcessZendeskTicketMutation {
  issueIntakeProcessZendeskTicket: {
    success: boolean;
    issues: {
      id: string;
      identifier: string;
      title: string;
      url: string;
    }[];
  };
}
